import { skipToken } from '@reduxjs/toolkit/query';
import { useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useFetchAccountQuery } from '@/slices/account-api-slice.ts';
import { useSecurityContext } from './security-provider.tsx';

/**
 * Syncs the current account with the `accountKey` route param and returns the
 * resolved account data.
 *
 * - On first land, if an account key is in the URL, sets it as the current account.
 * - Update the url whenever the user changes the current account so they can refresh
 *   and land on the same account.
 *
 * @param basePath The base route for this page, e.g. `/account/edit`.
 */
export function useRouteAccount({ basePath }: { basePath: string }) {
  const { accountKey } = useParams();
  const { currentAccount, visibleAccounts, setCurrentAccount } = useSecurityContext();
  const navigate = useNavigate();

  // Flag to ignore the current account change that happens when we sync the account with the URL on first land.
  const ignoreCurrentAccountChange = useRef(false);

  // On first land, if an account key is in the URL, sets it as the current account.
  useEffect(() => {
    if (visibleAccounts && accountKey && currentAccount?.key !== accountKey) {
      ignoreCurrentAccountChange.current = true;
      setCurrentAccount(visibleAccounts.find(x => x.key === accountKey));
    }
  }, [accountKey, visibleAccounts]);

  // Update the url whenever the user changes the current account so they can refresh
  // and land on the same account. Skip if the account change was triggered by the URL.
  useEffect(() => {
    if (ignoreCurrentAccountChange.current) {
      ignoreCurrentAccountChange.current = false;
      return;
    }

    if (!visibleAccounts) {
      return;
    }

    if (currentAccount?.key && currentAccount.key !== accountKey) {
      navigate(`${basePath}/${currentAccount.key}`, { replace: true });
      return;
    }

    if (!currentAccount && accountKey) {
      navigate(basePath, { replace: true });
    }
  }, [currentAccount?.key, visibleAccounts]);

  const {
    currentData: account,
    isLoading,
    error,
  } = useFetchAccountQuery(accountKey ?? currentAccount?.key ?? skipToken);

  useEffect(() => {
    if (error) {
      setCurrentAccount(null);
      navigate(basePath, { replace: true });
    }
  }, [error]);

  return { account, isLoading };
}
